/**
 * Admin API methods for party and API key management.
 * Used by admin role to manage writers and readers.
 */

import { api } from './client.js';

/**
 * List all parties managed by this admin.
 *
 * @returns {Promise<{parties: Array<Object>}>} List of party objects
 * @throws {ApiError} If unauthorized
 */
export async function listParties() {
  return api.get('/admin/parties');
}

/**
 * Get a specific party by ID.
 *
 * @param {string} partyId - The party ID to retrieve
 * @returns {Promise<Object>} Party object with details
 * @throws {ApiError} If party not found or unauthorized
 */
export async function getParty(partyId) {
  const path = `/admin/parties/${encodeURIComponent(partyId)}`;
  return api.get(path);
}

/**
 * Create a new party (writer or reader).
 *
 * @param {Object} params - Party creation parameters
 * @param {string} params.party_id - Unique party identifier
 * @param {string} params.role - Party role ('writer' or 'reader')
 * @param {string} [params.public_key] - Base64 public key (required for readers)
 * @returns {Promise<Object>} Created party object with API key
 * @throws {ApiError} If party ID already exists or unauthorized
 */
export async function createParty({ party_id, role, public_key }) {
  const body = { party_id, role };
  if (public_key !== undefined) {
    body.public_key = public_key;
  }
  return api.post('/admin/parties', body);
}

/**
 * Delete a party.
 *
 * @param {string} partyId - The party ID to delete
 * @returns {Promise<Object>} Deletion confirmation
 * @throws {ApiError} If party not found or unauthorized
 */
export async function deleteParty(partyId) {
  const path = `/admin/parties/${encodeURIComponent(partyId)}`;
  return api.delete(path);
}

/**
 * List all API keys for a party.
 *
 * @param {string} partyId - The party ID
 * @returns {Promise<{keys: Array<Object>}>} List of API key objects
 * @throws {ApiError} If party not found or unauthorized
 */
export async function listPartyKeys(partyId) {
  const path = `/admin/parties/${encodeURIComponent(partyId)}/keys`;
  return api.get(path);
}

/**
 * Create a new API key for a party.
 *
 * @param {string} partyId - The party ID
 * @returns {Promise<Object>} Created key object with plain API key
 * @throws {ApiError} If party not found or unauthorized
 */
export async function createPartyKey(partyId) {
  const path = `/admin/parties/${encodeURIComponent(partyId)}/keys`;
  return api.post(path, {});
}

/**
 * Revoke an API key for a party.
 *
 * @param {string} partyId - The party ID
 * @param {string} keyId - The key ID to revoke
 * @returns {Promise<Object>} Revocation confirmation
 * @throws {ApiError} If party or key not found, or unauthorized
 */
export async function revokePartyKey(partyId, keyId) {
  const path = `/admin/parties/${encodeURIComponent(partyId)}/keys/${encodeURIComponent(keyId)}`;
  return api.delete(path);
}
